import React, { useRef, useEffect } from "react";
import { Box, TextField } from "@mui/material";

interface OTPInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

const inputBoxStyles = {
  display: "flex",
  gap: 1,
  justifyContent: "center",
  my: 2,
};

const textFieldStyles = {
  width: "3rem",
  "& .MuiOutlinedInput-root": {
    color: "white",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.03)",
    "& fieldset": { borderColor: "rgba(255,255,255,0.06)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.12)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.5)" },
  },
  "& input": {
    padding: "12px 0",
  },
};

const inputPropsStyle = {
  textAlign: "center" as const,
  fontSize: "1.5rem",
  fontWeight: "bold",
  color: "white",
};

const OTPInput: React.FC<OTPInputProps> = ({
  value,
  onChange,
  disabled = false,
  error = false,
}) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.split("").concat(Array(6 - value.length).fill(""));

  useEffect(() => {
    if (inputRefs.current[0] && value.length === 0) {
      inputRefs.current[0].focus();
    }
  }, [value.length]);

  const handleChange = (index: number, digitValue: string): void => {
    if (disabled) return;

    if (!/^\d*$/.test(digitValue)) return;

    const newDigits = [...digits];
    newDigits[index] = digitValue.slice(-1);
    const newValue = newDigits.join("").replace(/\s/g, "");

    onChange(newValue);

    if (digitValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLDivElement>
  ): void => {
    if (e.key === "Backspace") {
      if (!digits[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (digits[index]) {
        const newDigits = [...digits];
        newDigits[index] = "";
        onChange(newDigits.join("").replace(/\s/g, ""));
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (/^\d{6}$/.test(pastedData)) {
      onChange(pastedData);
      inputRefs.current[5]?.focus();
    }
  };

  const handleFocus = (index: number): void => {
    inputRefs.current[index]?.select();
  };

  return (
    <Box sx={inputBoxStyles}>
      {digits.map((digit, index) => (
        <TextField
          key={index}
          inputRef={(el) => (inputRefs.current[index] = el)}
          value={digit || ""}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          disabled={disabled}
          error={error}
          inputProps={{
            maxLength: 1,
            style: inputPropsStyle,
            inputMode: "numeric" as const,
            pattern: "[0-9]*",
          }}
          sx={textFieldStyles}
        />
      ))}
    </Box>
  );
};

export default OTPInput;
